import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateBankDetails } from '../store/slices/bankSlice';
import { sendProfileUpdateNotification } from '../emailjs/emailService';
import { HiOutlineX } from 'react-icons/hi';
import './BankDetailsModal.scss';

const BankDetailsModal = ({ isOpen, onClose, bankDetails, userEmail }) => {
  const dispatch = useDispatch();
  const { updating, error } = useSelector(state => state.bank);
  const { user } = useSelector(state => state.auth);
  const [formData, setFormData] = useState({
    id: '',
    bankType: 'uk',
    accountName: '',
    accountNumber: '',
    bankName: '',
    branchName: '',
    routingNumber: '',
    swiftCode: '',
    sortCode: '',
    iban: ''
  });
  
  // Initialize form data when bankDetails change
  useEffect(() => {
    if (bankDetails) {
      setFormData({
        id: bankDetails.id || '',
        bankType: bankDetails.bankType || (bankDetails.iban ? 'international' : 'uk'),
        accountName: bankDetails.accountName || '',
        accountNumber: bankDetails.accountNumber || '',
        bankName: bankDetails.bankName || '',
        branchName: bankDetails.branchName || '',
        routingNumber: bankDetails.routingNumber || '',
        swiftCode: bankDetails.swiftCode || '',
        sortCode: bankDetails.sortCode || '',
        iban: bankDetails.iban || ''
      });
    }
  }, [bankDetails]);
  
  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'bankType') {
      setFormData(prev => ({
        ...prev,
        bankType: value,
        // Keep values but clear conflicting primary field for cleaner UX.
        sortCode: value === 'uk' ? prev.sortCode : '',
        iban: value === 'international' ? prev.iban : '',
      }));
      return;
    }
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Check what fields have changed
      const originalData = {
        bankType: bankDetails?.bankType || (bankDetails?.iban ? 'international' : 'uk'),
        accountName: bankDetails?.accountName || '',
        accountNumber: bankDetails?.accountNumber || '',
        bankName: bankDetails?.bankName || '',
        branchName: bankDetails?.branchName || '',
        routingNumber: bankDetails?.routingNumber || '',
        swiftCode: bankDetails?.swiftCode || '',
        sortCode: bankDetails?.sortCode || '',
        iban: bankDetails?.iban || ''
      };

      const normalizedFormData = {
        ...formData,
        sortCode: formData.bankType === 'uk' ? formData.sortCode : '',
        iban: formData.bankType === 'international' ? formData.iban : '',
      };
      
      const updatedFields = {};
      Object.keys(originalData).forEach(key => {
        if (normalizedFormData[key] !== originalData[key]) {
          updatedFields[key] = normalizedFormData[key];
        }
      });
      
      await dispatch(updateBankDetails(normalizedFormData)).unwrap();
      
      // Send email notification if there were changes
      if (Object.keys(updatedFields).length > 0 && user) {
        try {
          const emailResult = await sendProfileUpdateNotification(
            user,
            'bank_details',
            updatedFields
          );
          if (emailResult?.success) {
            console.log('Bank details update notification sent successfully');
          } else {
            console.error(
              'Failed to send bank details update notification:',
              emailResult?.error || 'Unknown email error'
            );
          }
        } catch (emailError) {
          console.error('Failed to send bank details update notification:', emailError);
          // Don't fail the whole operation if email fails
        }
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to update bank details:', error);
    }
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="modal-backdrop">
      <div className="modal-container">
        <div className="modal-header">
          <h2>Edit Bank Details</h2>
          <button className="close-button" onClick={onClose}>
            <HiOutlineX />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="bank-details-form">
          <div className="form-group">
            <label htmlFor="bankType">Bank Type</label>
            <select
              id="bankType"
              name="bankType"
              value={formData.bankType}
              onChange={handleChange}
              required
            >
              <option value="uk">UK-based Bank</option>
              <option value="international">International Bank</option>
            </select>
          </div>

          <div className="form-group">
            <label htmlFor="accountName">Account Holder Name</label>
            <input
              type="text"
              id="accountName"
              name="accountName"
              value={formData.accountName}
              onChange={handleChange}
              placeholder="Enter account holder name"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="accountNumber">Account Number</label>
            <input
              type="text"
              id="accountNumber"
              name="accountNumber"
              value={formData.accountNumber}
              onChange={handleChange}
              placeholder="Enter account number"
              required
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="bankName">Bank Name</label>
            <input
              type="text"
              id="bankName"
              name="bankName"
              value={formData.bankName}
              onChange={handleChange}
              placeholder="Enter bank name"
              required
            />
          </div>
          
          {formData.bankType === 'uk' ? (
            <div className="form-group">
              <label htmlFor="sortCode">Sort Code (UK banks only)</label>
              <input
                type="text"
                id="sortCode"
                name="sortCode"
                value={formData.sortCode}
                onChange={handleChange}
                placeholder="Enter 6-digit sort code"
                maxLength="6"
                pattern="[a-zA-Z0-9]{6}"
                title="Sort code must be 6 alphanumeric characters"
                required
              />
              <small className="field-hint">Required for UK bank accounts</small>
            </div>
          ) : (
            <div className="form-group">
              <label htmlFor="iban">IBAN (International banks)</label>
              <input
                type="text"
                id="iban"
                name="iban"
                value={formData.iban}
                onChange={handleChange}
                placeholder="Enter IBAN"
                required
              />
              <small className="field-hint">Required for international bank accounts</small>
            </div>
          )}
          
          <div className="form-group">
            <label htmlFor="branchName">Branch Name</label>
            <input
              type="text"
              id="branchName"
              name="branchName"
              value={formData.branchName}
              onChange={handleChange}
              placeholder="Enter branch name"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="routingNumber">Routing Number</label>
            <input
              type="text"
              id="routingNumber"
              name="routingNumber"
              value={formData.routingNumber}
              onChange={handleChange}
              placeholder="Enter routing number"
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="swiftCode">SWIFT Code</label>
            <input
              type="text"
              id="swiftCode"
              name="swiftCode"
              value={formData.swiftCode}
              onChange={handleChange}
              placeholder="Enter SWIFT code"
            />
          </div>
          
          {error && <div className="error-message">{error}</div>}
          
          <div className="form-actions">
            <button type="button" className="cancel-button" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="save-button" disabled={updating}>
              {updating ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BankDetailsModal; 